<template>
  <!-- Mahmoud El farargy, Oct 3rd, 2023 -->
  <!-- https://app.clickup.com/t/86bw1xznm -->
  <!-- Messages/Chats page -->
  <Page
    :title="`${$t('general.chats')} | Cezma`"
    description="View your messages on Cezma"
  >
    <section
      class="fixed top-[var(--main-header-height)] md:static md:top-auto bottom-0 left-0 right-0 w-full h-full bg-[#FFFFFF] app-container-full-width-mobile pt-0 pb-0 sm:pt-6 md:pt-8 xl:pt-10 2xl:pt-11 sm:pb-6 md:pb-7 lg:pb-8 xl:pb-10 2xl:pb-16 3xl:pb-17"
    >
      <div
        v-if="
          isLoadingContacts
            ? true
            : allChatContacts && allChatContacts.length > 0
        "
        class="relative flex gap-3 outer__chats__container md:max-h-[800px] md:h-[calc(100vh_-_var(--main-header-height)_-_2.75rem_-_2rem)]"
      >
        <!-- contacts side -->
        <ChatContacts
          :allChatContacts="sortedContacts"
          :isLoadingContacts="isLoadingContacts"
          :isLoadingMoreContacts="isLoadingMoreContacts"
          :reformatDate="reformatDate"
          :activeContactItem="activeContactItem"
          :loadMoreContacts="() => fetchContacts({ isLoadMore: true })"
          :contactsPageInfo="contactsPageInfo"
          :viewOnMobile="viewOnMobile"
          :lastVisitedRoute="lastVisitedRoute"
          :forwardedStore="forwardedStore"
          :updateForwardedStore="updateForwardedStore"
          :isMobileViewpoint="isMobileViewpoint"
          :switchViewOnMobile="switchViewOnMobile"
          ref="contactsRef"
        />
        
        <!-- messages side -->
        <ChatMessages
          :activeContactItem="activeContactItem"
          :isLoadingMessages="isLoadingMessages"
          :isLoadingContacts="isLoadingContacts"
          :isLoadingMoreMessages="isLoadingMoreMessages"
          :currentMessages="currentMessages"
          :onImageError="onImageError"
          :placeholderImg="placeholderImg"
          :reformatDate="reformatDateForMessages"
          :onMessageSubmission="onMessageSubmission"
          :isSendingMessage="isSendingMessage"
          :messageFail="messageFail"
          :contactsListLength="allChatContacts ? allChatContacts.length : 0"
          :loadMoreMessages="() => loadUserMessages({ isLoadMore: true })"
          :messagesPageInfo="messagesPageInfo"
          :viewOnMobile="viewOnMobile"
          :switchViewOnMobile="switchViewOnMobile"
          ref="messagesRef"
          :isDeletingContact="isDeletingContact"
          :allowChatOptions="
            isResellerOrSubReseller &&
            activeContactItem &&
            !activeContactItem.isForwarded
          "
          @onChatDeleteConfirmation="onChatDeleteConfirmation"
        />
      </div>
      <!-- shows when data is unavailable or empty -->
       <div
        v-if="(currentMessages.length === 0 && isLoadingMessages) || (failedToFetchContacts)"
        class="bg-[#ffffff00] h-[60vh] mt-10 md:mt-0 mb-20 border-gray-200 flex flex-col items-center justify-center px-4 rounded-lg"
      >
        <p
          class="text-6xl md:text-7xl lg:text-9xl font-bold tracking-wider text-gray-300"
        >
          <svg
            aria-hidden="true"
            class="mx-auto mb-4 text-gray-400 w-14 h-14"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
        </p>
        <p
          class="text-[18px] md:text-2xl lg:text-2xl font-bold text-gray-500 mt-4 text-center"
        >
          {{ $t("general.no_messages_available") }}
        </p>
        <button
          @click="$router.push('/')"
          class="flex items-center space-x-2 text-[14px] md:text-[16px] bg-blue-600 hover:bg-blue-700 text-gray-100 px-4 py-2 mt-6 rounded transition duration-150"
          :title="$t('general.back_home')"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            class="h-5 w-5"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fill-rule="evenodd"
              d="M9.707 14.707a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 1.414L7.414 9H15a1 1 0 110 2H7.414l2.293 2.293a1 1 0 010 1.414z"
              clip-rule="evenodd"
            ></path>
          </svg>
          <span>{{ $t("general.back_home") }}</span>
        </button>
      </div>
      
       <div v-if="currentMessages.length === 0 && !isLoadingMessages && allChatContacts.length === 0" class="loading-indicator flex items-center justify-center h-full">
        <div class="loader">
          <div class="loading-spinner border-4 border-t-4 border-gray-200 border-t-blue-500 rounded-full w-12 h-12 animate-spin"></div>
        </div>
      </div>
    </section>
  </Page>
</template>

<script>
import { PagePortal as Page, ChatContacts, ChatMessages } from "@/components";
import placeholderImg from "@/assets/images/placeholder(doNotRemove).jpg";
import moment from "moment-timezone";
import "@/helpers/moment-locales/ar";
import {
  SPECIFICROLES,
  MAINROLES,
  isEmpty,
  lowerString,
  onImageError,
} from "@/helpers";
import { mapState, mapActions } from "pinia";
import { useGlobalStore } from "@/store/Modules/global.js";
import checkUserAuthentication from "@/middleware/checkUserAuthentication.js";
import { Timestamp, collection, serverTimestamp } from "firebase/firestore";
import { getGroupDocuments, getUserDocuments, getMessages, addDocument, deleteGroup } from "@/helpers/Firebase-Setup/firebaseSetup";
export default {
  setup() {
    definePageMeta({
      layout: "user",
      middleware: checkUserAuthentication({
        specificRole: SPECIFICROLES.user,
        role: MAINROLES.user,
      }),
    });
  },
  name: "chats",
  data() {
    return {
      abortController: new AbortController(),
      isLoadingContacts: false,
      isLoadingMessages: false,
      isSendingMessage: false,
      isLoadingMoreContacts: false,
      failedToFetchContacts: false,
      isLoadingMoreMessages: false,
      viewOnMobile: "contacts",
      userObject: null,
      formattedCollections: [],
      smallScreenBreakpoint: 640,
      tabletScreenBreakpoint: 818,
      users: [],
      savedActiveContactId: "",
      allowForUpdatesChecking: true,
      isDeletingContact: false,
      checkContactsUpdatesInterval: undefined,
      lastVisitedRoute: "/",
      mediaQuery: window.matchMedia(`(max-width:818px)`),
      messagesPageOne: {
        chatId: "",
        list: [],
      },
      contactsPageOne: {
        list: [],
      },
      intervalSeconds: 15,
      messagesPageInfo: {
        currentPage: 1,
        totalPages: 2,
      },
      contactsPageInfo: {
        currentPage: 1,
        totalPages: 2,
      },
      messageFail: {
        status: false,
        error: "",
      },
      allChatContacts: [],
      currentMessages: [],
      placeholderImg,
      unsubscribeMessages: null,
      unsubscribeContacts: null,
      unsubscribeCollections: null,
    };
  },
  components: {
    Page,
    ChatContacts,
    ChatMessages,
  },
  computed: {
    ...mapState(useGlobalStore, {
      forwardedStore: (store) => store?.getForwardedStore,
      userRole: (store) => store?.localStorageCopy?.user?.role || {},
      disableChatting: (store) => !!store?.disableChatting,
    }),
    activeContactItem() {
      const route = useRoute();
      const currentChatId = route.query.id || this.savedActiveContactId;
      const item =
        Object.keys(this.forwardedStore)?.length > 0
          ? { isForwarded: true, ...this.forwardedStore }
          : this.allChatContacts?.find(
              (contact) => contact.id == currentChatId
            );
      return item || null;
    },
    isResellerOrSubReseller() {
      return (
        lowerString(this.userRole) == SPECIFICROLES.reseller ||
        lowerString(this.userRole) == SPECIFICROLES.moderator_reseller
      );
    },
    sortedContacts() {
      // contacts sorted from the newest to the oldest
      if(!this.activeContactItem?.id){
        // this.activeContactItemMethod();
      }
      return this.allChatContacts?.length > 0
        ? this.allChatContacts.sort(
            (a, b) =>
              new Date(b.last_message?.date).getTime() -
              new Date(a.last_message?.date).getTime()
          )
        : [];
    },
  },
  watch: {
    "activeContactItem.id": {
      immediate: true,
      handler(contactItemId) {
        if (contactItemId) {
          this.loadUserMessages();
          this.savedActiveContactId = contactItemId;
        }
      },
    },
    disableChatting: {
      immediate: true,
      handler(val) {
        // kicks the user out if the chatting is disabled
        if (typeof val === "boolean" && val) {
          this.$router.replace(this.ROUTESNAMES.user.homepage);
        }
      },
    },
  },
  methods: {
    ...mapActions(useGlobalStore, ["updateForwardedStore"]),
    isEmpty,
    onImageError,
    isMobileViewpoint() {
      return window.innerWidth <= this.smallScreenBreakpoint;
    },
    reformatDate(date) {
      const momentObj = moment(date * 1000).locale(this.currentlySelectedLanguage);
      const currentDate = moment();
      return momentObj.isSame(currentDate, "day")
        ? momentObj.fromNow()
        : momentObj.isSame(currentDate, "week")
        ? momentObj.format("dddd hh:mm A")
        : momentObj.format("dddd DD-MM-YYYY");
    },
    reformatDateForMessages(date) {
      const momentObj = moment(date?.seconds * 1000).locale(this.currentlySelectedLanguage);
      const currentDate = moment();
      return momentObj.isSame(currentDate, "day")
        ? momentObj.fromNow()
        : momentObj.isSame(currentDate, "week")
        ? momentObj.format("dddd hh:mm A")
        : momentObj.format("dddd DD-MM-YYYY");
    },
    switchViewOnMobile(newViewValue) {
      if (newViewValue != "contacts" && newViewValue != "messages") {
        return;
      }
      this.viewOnMobile = newViewValue;
    },
    updateMobileViewState(e) {
      if (e?.matches) {
        // in the desktop view
        document.body.style.overflowY = "visible";
      } else {
        // in the tablet view
        document.body.style.overflowY = "hidden";
      }
    },
    findDifferentItems(array1, array2) {
      const idSet = new Set();

      for (const item of array2) {
        idSet.add(item.id);
      }

      const differentItems = array1.filter(
        (item1) => !idSet.has(item1.id) && !item1.isPreviouslySend
      );
      return differentItems;
    },
    checkMessagesForUpdates() {
      // checks if there are any new messages, and if there are, add them to the existing messages
      const contactId = this.activeContactItem?.id;
      if (!contactId) {
        return;
      }

      this.makeRequest({
        specificRole: "",
        owner: this.MAINROLES.user,
        url: `${this.APIREQUESTS.user.getChatMessages.url}?chat_id=${contactId}&page=1`,
        method: this.APIREQUESTS.user.getChatMessages.method,
        abortControllerSignal: this.abortController.signal,
      }).then((res) => {
        // failed
        if (!res || !res?.hasOwnProperty("status") || !res?.status) {
          return;
        } else {
          // succeeded
          if (
            contactId == this.messagesPageOne?.id &&
            this.messagesPageOne?.list?.length > 0
          ) {
            const differentItems = this.findDifferentItems(
              res?.data,
              this.messagesPageOne.list
            );
            // adds newly send messages to the bottom of the messages list and scrolls to the bottom
            const sortedDiffMessagesFromOldestToLatest =
              differentItems?.length > 0
                ? differentItems.sort(
                    (a, b) =>
                      new Date(a.date).getTime() - new Date(b.date).getTime()
                  )
                : [];
            if (sortedDiffMessagesFromOldestToLatest?.length > 0) {
              this.currentMessages = [
                ...this.currentMessages?.filter((msg) => !msg.isPreviouslySend),
                ...sortedDiffMessagesFromOldestToLatest,
              ];

              const lastMessageItem = sortedDiffMessagesFromOldestToLatest[0];
              if (lastMessageItem) {
                this.modifyLatestMessageOfContact(
                  contactId,
                  lastMessageItem.content
                );
              }

              this.$nextTick(() => {
                this.messagesPageOne = {
                  id: this.messagesPageOne?.id,
                  list: res.data || [],
                };
                this.scrollMessagesToBottom({});
              });
            }
          }
        }
      });
    },
    makeUserOnline() {
      if (this.disableChatting) {
        return;
      }
      this.makeRequest({
        specificRole: "",
        owner: this.MAINROLES.user,
        url: this.APIREQUESTS.user.makeUserActive.url,
        method: this.APIREQUESTS.user.makeUserActive.method,
        abortControllerSignal: this.abortController.signal,
      }).then((res) => {
        // failed
        if (!res || !res?.hasOwnProperty("status") || !res?.status) {
          return;
        } else {
          // succeeded
        }
      });
    },
    scrollContactsToTop() {
      this.$refs?.contactsRef?.scrollToTop?.();
    },
    scrollMessagesToBottom({ ...args }) {
      this.$refs?.messagesRef?.scrollToBottom?.({ ...args });
    },
    checkContactsForUpdates(){
      this.allChatContacts = this.formattedCollections;
    },
    stopContactsCheckerInverval() {
      if (
        this.checkContactsUpdatesInterval &&
        this.checkContactsUpdatesInterval > 0
      ) {
        window.clearInterval(this.checkContactsUpdatesInterval);
        this.checkContactsUpdatesInterval = undefined;
      }
    },
    async readMessages(collectionId){
      return await updateDocument(collectionId, 'Groups', {lastMsg: {isRead: false, senderId: {$ne: this.userObject.userID}}}, {merge: true});
    },
    async loadUserMessages() {
      const route = useRoute();
      const currentChatId = route.query.id || this.savedActiveContactId;
      if(!this.userObject){
        await this.getUserTokenFirestore();
      }
      const unsubscribe = getMessages(this.userObject, currentChatId, (messages) => {
        const formattedMessages = messages.map(msg => ({
          ...msg,
          content: msg.textMsg,
          date: msg.sentAt,
          from_me: msg.senderId === this.userObject.userID
        }));
        this.currentMessages = formattedMessages.reverse();
        this.$nextTick(() => {
          this.scrollMessagesToBottom({});
        });
      });

      this.unsubscribeMessages = unsubscribe;
    },
    removeItemFromContacts(id) {
      if (this.allChatContacts?.length > 0) {
        const contactIndex = this.allChatContacts
          .map((el) => el.id)
          .indexOf(id);
        if (contactIndex >= 0) {
          this.allChatContacts.splice(contactIndex, 1);
        }
      }
    },
    fetchContacts() {
      this.fetchAllCollections();
      this.allChatContacts = this.formattedCollections;
    },
    modifyLatestMessageOfContact(contactId, message) {
      const foundContactItem = this.allChatContacts?.find(
        (contact) => contact.id == contactId
      );
      if (foundContactItem) {
        foundContactItem.last_message = {
          ...foundContactItem.last_message,
          date: new Date(),
          content: message,
        };
      }
    },
    async onMessageSubmission(message) {
      return new Promise(async (resolve, reject) => {
        if (!process.client) {
          reject();
          return;
        }
        const currentChatId = this.activeContactItem?.id;
        if (this.disableChatting || !message || !currentChatId) {
          reject();
          return;
        }
        this.isSendingMessage = true;

        this.messageFail = {
          status: false,
          error: "",
        };
        this.$nextTick(() => {
          this.scrollMessagesToBottom({});
        });
        const newlyFormattedMessageToSend = encodeURIComponent(message.trim());
        const { Timestamp } = await import("firebase/firestore");
        const messageDate = {
                "replyMessage": null,
                "gifUrl": "",
                "fileUrl": "",
                "location": null,
                "isRecAudio": false,
                "textMsg": message,
                "sentAt": Timestamp.now(),
                "type": "text",
                "senderId": this.userObject.userID,
                "isRead": false,
                "groupUpdate": null,
                "isForwarded": false,
                "videoThumbnail": ""
              };
        const messageId = await addDocument(currentChatId, 'Messages', messageDate, this.userObject);
        this.isSendingMessage = false;
        if(messageId){
          this.loadUserMessages();
          resolve();
        }else{
          reject();
          this.messageFail = {
            status: true,
            error: this.$t("general.message_sending_failed"),
          };
          this.isSendingMessage = false;
          return;
        }
      });
    },
    async onChatDeleteConfirmation(id) {
      if (this.disableChatting || !id) {
        return;
      }

      this.isDeletingContact = true;
      const deleteGroupResponse = await deleteGroup(id);
      if(deleteGroupResponse){
        this.isDeletingContact = false;
        this.notify({
          type: "success",
          msg: this.$t("general.chat_deleted_successfully"),
        });
        this.$router.push(`${this.ROUTESNAMES.user.chats}`).then(() => {
          const router = useRouter();
          router.go();
        });
      }else{
        this.isDeletingContact = false;
        this.notify({
          type: "error",
          msg: this.$t("general.chat_deletion_failed"),
        });
      }
    },
    mergeUsersAndCollections(users, collections) {
      // Create map using 'id' as key (consistent with getUserDocuments return structure)
      // Also support 'userId' if it exists in user data
      const userMap = new Map();
      users.forEach(user => {
        if (user.id) {
          userMap.set(user.id, user);
        }
        if (user.userId && user.userId !== user.id) {
          userMap.set(user.userId, user);
        }
      });

      const mergedCollections = collections.map(collection => {
        // Handle adminMembers - can be array of IDs or objects with userId
        const adminMembersInfo = collection.adminMembers 
          ? collection.adminMembers.map(member => {
              const memberId = typeof member === 'string' ? member : member?.userId;
              return memberId ? userMap.get(memberId) : null;
            }).filter(Boolean)
          : [];
        
        // Handle messages - only process if messages exist (not fetched for contacts list)
        const messagesSender = collection.messages && collection.messages.length > 0
          ? collection.messages.map(message => userMap.get(message.senderId)).filter(Boolean)
          : [];
        
        // Handle members - array of user IDs
        const members = collection.members 
          ? collection.members.map(memberId => userMap.get(memberId)).filter(Boolean)
          : [];
        
        collection.members = members;
        collection.adminMembers = adminMembersInfo;
        collection.isOwner = collection.customerId === this.userObject.userID;
        
        // Safely handle lastMsg
        if (collection.lastMsg) {
          collection.lastMsg.fromMe = collection.lastMsg.senderId === this.userObject.userID;
        }
        
        // Only process messages if they exist
        if (collection.messages && collection.messages.length > 0) {
          collection.messages.forEach(message => {
            const sender = userMap.get(message.senderId);
            message.senderName = sender?.fullname || sender?.userId || message.senderId;
          });
        }
        
        return collection;
      });

      return mergedCollections;
    },
    getUserTokenFirestore() {
      return new Promise((resolve, reject) => {
        const UID = useCookie?.("UID")?.value ? useCookie?.("UID")?.value : false;
        if (!UID) {
          reject("No UID found");
          return;
        }
        this.makeRequest({
          owner: this.MAINROLES.user,
          url: `${this.APIREQUESTS.user.getTokenFirestore.url}/${UID}`,
          method: this.APIREQUESTS.user.getTokenFirestore.method,
        }).then((res) => {
          // failed
          if(!res || !res?.data){
            this.notify({
              type: "error",
              msg: this.$t("general.something_went_wrong"),
            });
            reject("No data found");
            return;
          }else{
            const token = res?.data?.authToken;
            const userID = res?.data?.userId;
            this.userObject = {
              token: token,
            userID: userID,
            storeIDs: res?.data?.storeIDs || [],
            };
            resolve(this.userObject);
          }
        })
        .catch((err) => {
          this.notify({
            type: "error",
            msg: this.$t("general.something_went_wrong"),
          });
          this.isLoadingMessages = false;
          this.currentMessages = [];
          reject(err);
        });
      });
    },
    async fetchAllCollections() {
      try {
        // Set loading state
        this.isLoadingContacts = true;
        this.failedToFetchContacts = false;
        
        // Ensure userObject is available (cache to avoid redundant calls)
        if (!this.userObject) {
          await this.getUserTokenFirestore();
        }
        
        // Extract user IDs from collections and fetch users in parallel
        const extractUserIds = (collections) => {
          const userIds = new Set();
          collections.forEach(collection => {
            // Add members
            if (collection.members && Array.isArray(collection.members)) {
              collection.members.forEach(memberId => userIds.add(memberId));
            }
            // Add admin members
            if (collection.adminMembers && Array.isArray(collection.adminMembers)) {
              collection.adminMembers.forEach(member => {
                if (typeof member === 'string') {
                  userIds.add(member);
                } else if (member?.userId) {
                  userIds.add(member.userId);
                }
              });
            }
            // Add customer ID
            if (collection.customerId) {
              userIds.add(collection.customerId);
            }
            // Add sender ID from last message
            if (collection.lastMsg?.senderId) {
              userIds.add(collection.lastMsg.senderId);
            }
          });
          return Array.from(userIds);
        };
        
        // Set up group documents listener (without fetching messages for performance)
        const unsubscribeCollections = getGroupDocuments(
          this.userObject, 
          async (collections) => {
            try {
              // Extract user IDs from collections
              const userIds = extractUserIds(collections);
              
              // Fetch users in parallel with groups (optimized to only fetch relevant users)
              const usersPromise = getUserDocuments(this.userObject?.token, userIds.length > 0 ? userIds : null);
              
              // Wait for users to be fetched
              this.users = await usersPromise;
              
              // Merge collections with user data
              const mergedCollections = this.mergeUsersAndCollections(this.users, collections);
              this.formattedCollections = this.formatCollectionsForContacts(mergedCollections);
              this.allChatContacts = this.formattedCollections.reverse();
              
              // Update loading state
              this.isLoadingContacts = false;
              
              // Check if data is available
              if(this.allChatContacts.length === 0 || this.users.length === 0 || this.formattedCollections.length === 0 || !this.userObject){
                this.failedToFetchContacts = true;
              }
            } catch (error) {
              console.error("Error processing collections: ", error);
              this.isLoadingContacts = false;
              this.failedToFetchContacts = true;
            }
          },
          { includeMessages: false } // Don't fetch messages for contacts list
        );

        this.unsubscribeContacts = unsubscribeCollections;
      } catch (error) {
        console.error("Error fetching all collections: ", error);
        this.isLoadingContacts = false;
        this.failedToFetchContacts = true;
      }
    },
    isReseller(collection){
      return collection.store && this.userObject?.storeIDs?.includes(collection.store.storeId) ? true : false;
    },
    syncOnlineStatus(collection){
      if(this.isReseller(collection)){
        // adminMembers are now user objects after mergeUsersAndCollections
        return collection.adminMembers && collection.adminMembers.length > 0
          ? collection.adminMembers.some(member => member?.isOnline)
          : false;
      }else{
        // members are now user objects after mergeUsersAndCollections
        return collection.members && collection.members.length > 0
          ? collection.members.some(member => member?.isOnline)
          : false;
      }
    },
    syncUnreadCount(collection){
      return collection.messages ? collection.messages.filter(message => !message.isRead && message.senderId !== this.userObject.userID).length : 0;
    },
    syncUnreadList(collection){
      return collection.unreadList ? collection.unreadList[this.userObject.userID] : null;
    },
    getUserName(userId){
      return this.users.find(user => user.id === userId)?.fullname || '';
    },
    getUserImage(userId){
      
      return this.users.find(user => user.id === userId)?.photoUrl || '';
    },
    formatCollectionsForContacts(collections) {
        return collections.map(collection => {
            return {
                id: collection.id, 
                // name: collection.name,
                name: !this.isReseller(collection) ? collection?.store?.storeName : this.getUserName(collection?.customerId),
                image: !this.isReseller(collection) ? collection?.store?.storeLogo : this.getUserImage(collection?.customerId), 
                is_store: !this.isReseller(collection), 
                store_id: this.isReseller(collection) ? null : collection?.store?.storeId, 
                store_username: !this.isReseller(collection) ? collection?.store?.storeUserName : this.getUserName(collection?.customerId), 
                verified: 0, 
                product: {
                    id: collection.id, 
                    name_ar: collection.description,
                    name_en: collection.name, 
                    description_ar: collection.description, 
                    description_en: collection.description, 
                    image: collection.photoUrl || "", 
                    images: [], 
                    category: {
                        id: null, 
                        name_ar: "", 
                        name_en: "", 
                        has_price: 0, 
                        parent_id: null, 
                        image: "" 
                    }
                },
                attributes: [], 
                branch: null, 
                last_message: {
                    id: null, 
                    content: collection.lastMsg?.textMsg || "", 
                    type: collection.lastMsg?.type || "text", 
                    from_me: collection.lastMsg?.senderId === this.userObject.userID || false, 
                    date: collection.lastMsg?.sentAt.seconds || ""
                },
                unread_count: this.syncUnreadList(collection), 
                unread_list: this.syncUnreadList(collection),
                active: this.syncOnlineStatus(collection), 
                seen: collection.lastMsg?.isRead || false
            };
        });
    }
  },
  beforeMount() {
    const router = useRouter();
    this.lastVisitedRoute = router?.options?.history?.state?.back || "/";
    this.getUserTokenFirestore();
  },
  mounted() {
    this.makeUserOnline();
    this.fetchAllCollections();
    this.loadUserMessages();

    // Note: the matchMedia might not work with all browsers but it's better than using the resize event anyway
    this.mediaQuery = window.matchMedia(
      `(min-width: ${this.tabletScreenBreakpoint}px)`
    );
    this.updateMobileViewState(this.mediaQuery);
    this.mediaQuery.addEventListener("change", this.updateMobileViewState);

  },
  beforeUnmount() {
    // prevents API requests from loading after going away from the page
    this.abortController.abort();

    document.body.style.overflowY = "visible";
    this.mediaQuery.removeEventListener("change", this.updateMobileViewState);
    this.stopContactsCheckerInverval();

    if (this.unsubscribeMessages) {
      this.unsubscribeMessages();
    }
    if (this.unsubscribeContacts) {
      this.unsubscribeContacts();
    }
    if (this.unsubscribeCollections) {
      this.unsubscribeCollections();
    }
  },
};
</script>

<style lang="scss">
.elevated__parent {
  position: relative;

  .elevated__bottom,
  .elevated__top {
    width: 100%;
    height: 23px;
    position: absolute;
    pointer-events: none;
    left: 0;
    display: flex;
    flex-direction: column;
    align-items: stretch;
    padding: 0;
    margin: 0;
    flex-shrink: 0;
    border: 0;
    vertical-align: baseline;
    font-size: 100%;
    border-radius: 0;
    z-index: 0;
  }

  .elevated__top {
    background: linear-gradient(
      to top,
      rgba(var(--elevated-background), 0) 0,
      rgb(var(--elevated-background))
    );
    top: 0;
    z-index: 0;
  }

  .elevated__bottom {
    background: linear-gradient(
      to bottom,
      rgba(var(--elevated-background), 0) 0,
      rgb(var(--elevated-background))
    );
    bottom: 0;
  }

  @media only screen and (max-width: 640px) {
    .elevated__bottom,
    .elevated__top {
      height: 16px;
    }
  }
}

.outer__chats__container {
  @media only screen and (max-width: 819px) {
    height: 89vh;
    height: 89svh;
    height: 89dvh;
  }

  @media only screen and (max-width: 634px) {
    height: 91vh;
    height: 91svh;
    height: 91dvh;
  }
}

.chat__scrollbar {
  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    border-radius: 10px;
    background: #f6f7f7;
  }

  &::-webkit-scrollbar-thumb {
    border-radius: 10px;
    background: var(--scrollbar-default-clr);
    transition: background 0.2s linear;

    &:hover {
      background: var(--scrollbar-on-hover-clr);
    }
  }

  @media only screen and (min-width: 780px) {
    scrollbar-gutter: stable;
  }
}

.centered__loader {
  display: flex;
  justify-items: center;
  margin: auto;
  width: 25px;
  height: 25px;
  border: 3.5px solid var(--gray-2);
  border-bottom-color: transparent;
  border-radius: 50%;
  display: inline-block;
  box-sizing: border-box;
  animation: rotation 1s linear infinite;

  @keyframes rotation {
    0% {
      transform: rotate(0deg);
    }

    100% {
      transform: rotate(360deg);
    }
  }
}

@media only screen and (max-width: 680px) {
  .widget-visible,
  .widget-visible body {
    display: none !important;
  }
}
</style>
